/**
 * MPR REPORT COMPONENT
 */

import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { DynamicFormComponent } from '../../../core/shared/dynamic-form/dynamic-form.component';
import { Popup } from '../../../core/shared/components/popup/popup';
import { Setup } from '../../setup/shared/service/setup';
import { ReportService } from '../shared/services/report.service';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const EXCEL_EXTENSIONS = ['.xls', '.xlsx'];
const EXCEL_MIME_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

@Component({
  selector: 'app-mpr-report',
  standalone: true,
  imports: [CommonModule, DynamicFormComponent, Popup, TranslatePipe],
  template: `
    <div class="p-6 space-y-6">
      <!-- Page Header -->
      <div>
        <h1 class="text-lg sm:text-xl font-semibold text-[#1E2A78]">
          {{ 'MPR.TITLE' | translate }}
        </h1>
        <p class="text-sm text-gray-500 mt-1">{{ 'MPR.SUBTITLE' | translate }}</p>
      </div>

      <!-- Filters -->
      <div class="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div class="mb-5">
          <h3 class="text-lg font-semibold text-[#1E2A78]">{{ 'MPR.FILTERS_TITLE' | translate }}</h3>
          <p class="text-sm text-gray-500 mt-1">{{ 'MPR.FILTERS_DESC' | translate }}</p>
        </div>

        <app-dynamic-form
          [fields]="filterFields"
          [model]="filterModel"
          (formChange)="onFilterChange($event)"
        ></app-dynamic-form>
      </div>

      <!-- Upload -->
      <div class="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
        <div class="mb-5">
          <h3 class="text-lg font-semibold text-[#1E2A78]">{{ 'MPR.UPLOAD_TITLE' | translate }}</h3>
          <p class="text-sm text-gray-500 mt-1">{{ 'MPR.UPLOAD_DESC' | translate }}</p>
        </div>

        @if (!selectedFile) {
          <!-- Drop Zone -->
          <div
            (click)="fileInput.click()"
            class="border-2 border-dashed border-gray-300 rounded-2xl h-44 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 transition"
          >
            <img src="images/upload.png" class="w-12 mb-2 opacity-70" />
            <p class="text-gray-500 text-sm">{{ 'MPR.CLICK_TO_UPLOAD' | translate }}</p>
          </div>
        } @else {
          <!-- Selected File -->
          <div
            class="flex items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3"
          >
            <div class="flex items-center gap-3 min-w-0">
              <span class="text-2xl">📄</span>
              <span class="text-sm text-gray-700 truncate">{{ selectedFile.name }}</span>
            </div>

            <button
              type="button"
              (click)="removeFile()"
              class="text-xs text-red-500 hover:underline shrink-0"
            >
              {{ 'MPR.REMOVE' | translate }}
            </button>
          </div>
        }

        <!-- Error -->
        @if (fileError) {
          <div class="text-red-500 text-sm mt-3">
            {{ fileError | translate }}
          </div>
        }

        <input
          #fileInput
          type="file"
          accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          class="hidden"
          (change)="onFileSelected($event)"
        />

        <div class="flex gap-4 mt-5">
          <button
            type="button"
            [disabled]="!selectedFile || uploading"
            (click)="onUpload()"
            class="px-6 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ 'MPR.UPLOAD_BUTTON' | translate }}
          </button>
        </div>
      </div>
    </div>
    <!-- LNLY_AGNT: not allowed to submit at all — single OK action, no close icon,
         no backdrop-click, no Escape (self-contained overlay, unchanged). -->
    @if (showValidationModal && isSubmissionRestricted) {
      <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
        <div
          class="flex max-h-[90vh] w-full max-w-lg min-w-[300px] flex-col rounded-[20px] bg-white shadow-xl sm:rounded-[24px]"
        >
          <div class="border-b px-6 py-4 sm:px-8 sm:py-6">
            <h2 class="text-lg font-bold text-[#0B0D5C] sm:text-xl">
              {{ 'MPR.VALIDATION_TITLE' | translate }}
            </h2>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
            <ng-container *ngTemplateOutlet="validationBody"></ng-container>
          </div>

          <div
            class="flex flex-col-reverse gap-3 border-t px-6 py-4 sm:flex-row sm:justify-end sm:px-8"
          >
            <button type="button" (click)="onAcknowledgeValidation()" class="btn-primary">
              {{ 'MPR.OK' | translate }}
            </button>
          </div>
        </div>
      </div>
    }

    <!-- All other roles: same popup UI, rebuilt on the shared app-popup component. -->
    <app-popup
      [show]="showValidationModal && !isSubmissionRestricted"
      (showChange)="onValidationPopupVisibilityChange($event)"
      type="custom"
      title="MPR.VALIDATION_TITLE"
      width="w-full max-w-lg min-w-[300px]"
    >
      <ng-container *ngTemplateOutlet="validationBody"></ng-container>

      <div class="mt-6 flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:justify-end">
        <button
          type="button"
          [disabled]="submitting"
          (click)="onCancelValidation()"
          class="btn-outline disabled:cursor-not-allowed disabled:opacity-50"
        >
          {{ 'MPR.CANCEL' | translate }}
        </button>

        <button
          type="button"
          [disabled]="submitting || !validationPassed"
          [title]="!validationPassed ? ('MPR.SUBMIT_BLOCKED_TITLE' | translate) : ''"
          (click)="onSaveValidation()"
          class="btn-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-indigo-600"
        >
          {{ 'MPR.SUBMIT_BUTTON' | translate }}
        </button>
      </div>
    </app-popup>

    <!-- Shared between both popups so the body markup is defined exactly once. -->
    <ng-template #validationBody>
      <div
        class="flex items-center gap-4 rounded-xl p-4"
        [class.bg-emerald-50]="validationPassed"
        [class.bg-red-50]="!validationPassed"
      >
        <span
          class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-xl"
          [class.bg-emerald-100]="validationPassed"
          [class.text-emerald-600]="validationPassed"
          [class.bg-red-100]="!validationPassed"
          [class.text-red-600]="!validationPassed"
        >
          {{ validationPassed ? '✅' : '⚠️' }}
        </span>

        <div>
          <span
            class="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase"
            [class.bg-emerald-100]="validationPassed"
            [class.text-emerald-700]="validationPassed"
            [class.bg-red-100]="!validationPassed"
            [class.text-red-700]="!validationPassed"
          >
            {{ (validationPassed ? 'MPR.STATUS_PASSED' : 'MPR.STATUS_FAILED') | translate }}
          </span>

          <p class="mt-1.5 text-sm text-gray-600">
            {{
              (validationPassed
                ? 'MPR.VALIDATION_PASSED_MESSAGE'
                : 'MPR.VALIDATION_FAILED_MESSAGE'
              ) | translate
            }}
          </p>
        </div>
      </div>

      @if (!validationPassed && failedRuleCodes.length > 0) {
        <div class="mt-5">
          <p class="text-sm font-semibold text-gray-700">
            {{ 'MPR.VALIDATION_ERRORS_LABEL' | translate }}
          </p>

          <ul
            class="mt-2 max-h-56 space-y-2 overflow-y-auto rounded-xl border border-red-100 bg-red-50/60 p-3"
          >
            @for (ruleCode of failedRuleCodes; track $index) {
              <li
                class="flex items-start gap-2 rounded-lg bg-white px-3 py-2 text-sm text-gray-700 shadow-sm"
              >
                <span class="mt-0.5 shrink-0 text-red-500">❌</span>
                <span class="break-words">{{ ruleCode }}</span>
              </li>
            }
          </ul>
        </div>
      }
    </ng-template>
  `,
})
export class MprReportComponent implements OnInit {
  private setupService = inject(Setup);
  private reportService = inject(ReportService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);
  @ViewChild(DynamicFormComponent) private dynamicFormRef?: DynamicFormComponent;
  institutionOptions: { label: string; value: number }[] = [];
  filterFields: DynamicFieldConfig[] = [];
  filterModel: any = {};
  selectedFile: File | null = null;
  fileError = '';
  uploading = false;
  showValidationModal = false;
  validationPassed = false;
  failedRuleCodes: string[] = [];
  submitting = false;
  // LNLY_AGNT is not allowed to submit the report — the popup shows a
  // single OK action for that role instead of Save/Cancel.
  isSubmissionRestricted = false;
  private pendingClntSeq = 0;
  private pendingMnth = '';

  ngOnInit(): void {
    this.buildFields();

    this.translate.onLangChange.subscribe(() => {
      this.buildFields();
    });

    this.setupService.getclientList().subscribe((clients: any[]) => {
      this.institutionOptions = clients
        .map((c) => ({
          label: (c.clntNm || '').trim(),
          value: c.clientSeq,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

      this.filterFields = this.filterFields.map((field) =>
        field.key === 'clientSeq' ? { ...field, options$: this.institutionOptions } : field,
      );
    });
  }

  private get currentRole(): string {
    return (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
      .trim()
      .toUpperCase();
  }

  private buildFields(): void {
    this.filterFields = [
      ...(this.currentRole === 'LNLY_AGNT'
        ? []
        : [
            {
              key: 'clientSeq',
              label: this.translate.instant('MPR.INSTITUTION'),
              type: 'select' as const,
              options$: this.institutionOptions,
              validations: { required: true },
              gridClass: 'col-span-1',
            },
          ]),
      {
        key: 'month',
        label: this.translate.instant('MPR.MONTHLY'),
        type: 'select',
        // Only the current and past months of the current year — future months can't be reported yet.
        options$: MONTHS.slice(0, new Date().getMonth() + 1).map((month) => ({
          label: this.translate.instant(`MPR.${month.toUpperCase()}`),
          value: month,
        })),
        validations: { required: true },
        gridClass: 'col-span-1',
      },
    ];
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!EXCEL_EXTENSIONS.includes(extension) && !EXCEL_MIME_TYPES.includes(file.type)) {
      this.fileError = 'MPR.INVALID_FILE_TYPE';
      this.selectedFile = null;
      input.value = '';
      return;
    }

    this.fileError = '';
    this.selectedFile = file;
    input.value = '';
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileError = '';
  }

  onFilterChange(value: any): void {
    this.filterModel = { ...this.filterModel, ...value };
  }

  /** Clears the Institution and Monthly selections right after a successful upload. */
  private resetFilterSelections(): void {
    // `reset()` (unlike `patchValue()`) also marks every control pristine and
    // untouched, so the required-field errors don't reappear immediately.
    this.dynamicFormRef?.form?.reset({ clientSeq: null, month: null });
    this.filterModel = {};
  }

  onUpload(): void {
    if (!this.selectedFile || this.uploading) {
      return;
    }
    const isAgent = this.currentRole === 'LNLY_AGNT';
    const formValue = this.dynamicFormRef?.form?.value ?? {};
    const month = formValue.month;
    if (!month || (!isAgent && !formValue.clientSeq)) {
      this.toastr.error(this.translate.instant('MPR.SELECT_REQUIRED_FIELDS'));
      return;
    }
    const clntSeq = isAgent ? 0 : Number(formValue.clientSeq);
    const mnth = `${month}-${new Date().getFullYear()}`;
    this.uploading = true;
    this.reportService.uploadMprReport(clntSeq, mnth, this.selectedFile).subscribe({
      next: (res: any) => {
        this.uploading = false;
        this.pendingClntSeq = clntSeq;
        this.pendingMnth = mnth;
        this.isSubmissionRestricted = isAgent;
        this.resetFilterSelections();
        // MPR API reports the outcome as `success`, not QPR's `validationPassed`.
        this.validationPassed = !!(res?.validationPassed ?? res?.success);
        this.failedRuleCodes = Array.isArray(res?.failedRuleCodes) ? res.failedRuleCodes : [];
        this.showValidationModal = true;
      },
      error: (err) => {
        this.uploading = false;
        console.error('MPR report upload failed:', err);
        this.toastr.error(this.translate.instant('MPR.UPLOAD_FAILED'));
      },
    });
  }

  /** LNLY_AGNT's single "OK" action — just closes the popup, no submit call. */
  onAcknowledgeValidation(): void {
    this.showValidationModal = false;
    this.removeFile();
  }

  /** Also fires when the shared app-popup's own close (✕) is used — treated
   * the same as Cancel: just close, no API call. */
  onValidationPopupVisibilityChange(visible: boolean): void {
    if (!visible) {
      this.showValidationModal = false;
    }
  }

  onSaveValidation(): void {
    // Failed processing status blocks Submit — enforced here too, not just
    // via the disabled attribute, in case the button is reachable another way.
    if (!this.validationPassed) {
      this.toastr.error(this.translate.instant('MPR.SUBMIT_BLOCKED_MESSAGE'));
      return;
    }

    this.submitReport();
  }

  /** Cancel only closes the popup — no API call, no data changes. */
  onCancelValidation(): void {
    this.showValidationModal = false;
  }

  private submitReport(): void {
    if (this.submitting) {
      return;
    }

    this.submitting = true;

    this.reportService
      .submitMprReport({ clntSeq: this.pendingClntSeq, mnth: this.pendingMnth, aprvdFlg: 1 })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.showValidationModal = false;
          this.toastr.success(this.translate.instant('MPR.SUBMIT_SUCCESS'));
          this.removeFile();
        },
        error: (err) => {
          this.submitting = false;
          console.error('MPR report submit failed:', err);
          this.toastr.error(this.translate.instant('MPR.SUBMIT_FAILED'));
        },
      });
  }
}
